import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Volume2, Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const { t } = useLanguage();

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('ai', 'welcome') }]);
    }
  }, [isOpen]);

  const handleSend = async (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, { message: msg, session_id: sessionId });
      if (!sessionId) setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error(t('ai', 'error'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  // TTS - Read message aloud
  const playTTS = async (text, msgIdx) => {
    setTtsLoading(msgIdx);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/tts`, { text }, { responseType: 'blob' });
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => { URL.revokeObjectURL(audioUrl); setTtsLoading(null); };
      audio.onerror = () => setTtsLoading(null);
      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Errore nella riproduzione vocale');
      setTtsLoading(null);
    }
  };

  // STT - Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size < 100) return;

        setLoading(true);
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'voice.webm');
          const response = await axios.post(`${BACKEND_URL}/api/stt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (response.data.text) {
            await handleSend(response.data.text);
          }
        } catch (error) {
          console.error('STT error:', error);
          toast.error('Errore nella trascrizione vocale');
          setLoading(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Impossibile accedere al microfono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#0066b2] to-[#005494] text-white rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center" data-testid="ai-assistant-open-button">
          <MessageCircle className="w-8 h-8" />
          <span className="absolute top-0 right-0 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200" data-testid="ai-assistant-chat-window">
          <div className="bg-gradient-to-r from-[#0066b2] to-[#005494] text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
              <div><div className="font-semibold">{t('ai', 'title')}</div><div className="text-xs text-white/80">{t('ai', 'subtitle')}</div></div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors" data-testid="ai-assistant-close-button"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-testid="ai-assistant-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`ai-message-${msg.role}-${idx}`}>
                <div className={`max-w-[80%] rounded-2xl ${msg.role === 'user' ? 'bg-[#0066b2] text-white rounded-br-none' : 'bg-white text-gray-800 shadow-sm rounded-bl-none'}`}>
                  <p className="text-sm leading-relaxed p-3">{msg.content}</p>
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => playTTS(msg.content, idx)}
                      disabled={ttsLoading !== null}
                      className="px-3 pb-2 pt-0 text-gray-400 hover:text-[#0066b2] transition-colors disabled:opacity-50"
                      data-testid={`tts-button-${idx}`}
                    >
                      {ttsLoading === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="ai-assistant-loading">
                <div className="bg-white text-gray-800 p-3 rounded-2xl rounded-bl-none shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder={t('ai', 'placeholder')} className="flex-1" disabled={loading || isRecording} data-testid="ai-assistant-input" />
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={loading}
                className={`px-3 rounded-lg border transition-all ${isRecording ? 'bg-red-500 text-white border-red-500 animate-pulse' : 'bg-white text-gray-500 border-gray-200 hover:text-[#0066b2] hover:border-[#0066b2]'}`}
                data-testid="ai-assistant-mic-button"
              >
                {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <Button onClick={() => handleSend()} disabled={loading || !input.trim() || isRecording} className="bg-[#0066b2] hover:bg-[#005494]" data-testid="ai-assistant-send-button"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
